import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Crown, Shield, User } from "lucide-react";
import { api, mediaUrl } from "@/lib/api";
import { NATIONAL_EXECUTIVES, EXCO_HIERARCHY, LGAS, ALL_EXCO_ROLES } from "@/lib/data";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

function LeaderCard({ roleTitle, desc, person, highlight }) {
  return (
    <motion.div initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
      className={`rounded-2xl border bg-[#131519] overflow-hidden ${highlight ? "border-[#E6197E]/50 shadow-lg shadow-[#E6197E]/10" : "border-white/10"}`}>
      <div className="aspect-[4/3] bg-zinc-800 overflow-hidden relative flex items-center justify-center">
        {person?.photo ? <img src={mediaUrl(person.photo)} alt={person.name} loading="lazy" className="w-full h-full object-cover" /> :
          <User className="w-12 h-12 text-zinc-700" />}
        {highlight && <div className="absolute top-3 right-3"><Crown className="w-5 h-5 text-amber-400" /></div>}
      </div>
      <div className="p-5">
        <div className="font-heading font-semibold text-base leading-tight">{roleTitle}</div>
        {person ? <div className="text-sm text-[#E6197E] font-medium mt-1">{person.name}</div> : <div className="text-xs text-zinc-500 mt-1">Position open</div>}
        {(person?.bio || desc) && <p className="text-xs text-zinc-400 mt-2 leading-relaxed">{person?.bio || desc}</p>}
      </div>
    </motion.div>
  );
}

export default function Exco() {
  const [dbExco, setDbExco] = useState([]);
  const [lga, setLga] = useState("Lafia");

  useEffect(() => { api.get("/exco").then((r) => setDbExco(r.data)); }, []);

  const findPerson = (role, scope, lgaName) =>
    dbExco.find((e) => e.role === role && e.scope === scope && (scope === "national" || e.lga === lgaName));

  const knownRoles = new Set([...ALL_EXCO_ROLES, ...NATIONAL_EXECUTIVES.map((n) => n.role)]);
  const extraNational = dbExco.filter((e) => e.scope === "national" && !knownRoles.has(e.role));
  const extraLga = dbExco.filter((e) => e.scope === "lga" && e.lga === lga && !knownRoles.has(e.role));

  return (
    <main className="pt-28 pb-20 min-h-screen">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <div className="overline-label mb-3">Leadership</div>
        <h1 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight flex items-center gap-3"><Shield className="w-8 h-8 text-[#E6197E]" /> EXCO & Coordinators</h1>
        <p className="text-zinc-400 mt-3 max-w-xl">The executive structure driving the Nasarawa Expo creative movement, at national level and across all 13 Local Government Areas.</p>

        <Tabs defaultValue="national" className="mt-10">
          <TabsList className="bg-[#131519] border border-white/10">
            <TabsTrigger value="national" data-testid="exco-national-tab">National</TabsTrigger>
            <TabsTrigger value="lga" data-testid="exco-lga-tab">Local Government</TabsTrigger>
          </TabsList>

          <TabsContent value="national" className="mt-10" data-testid="exco-national-section">
            <h2 className="font-heading text-xl font-semibold mb-6 flex items-center gap-2"><Crown className="w-5 h-5 text-amber-400" /> Executive Command</h2>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5 mb-14">
              <LeaderCard roleTitle="Chairman (Convener)" desc="Top Executive Officer; ultimate decision-making authority." highlight person={findPerson("Chairman (Convener)", "national")} />
              <LeaderCard roleTitle="Assistant Chairman (Deputy)" desc="Second-in-Command; monitors internal committee performance." highlight person={findPerson("Assistant Chairman", "national")} />
              {NATIONAL_EXECUTIVES.map((n) => (
                <LeaderCard key={n.role} roleTitle={`${n.role} — ${n.title}`} desc={n.desc} highlight person={findPerson(n.role, "national")} />
              ))}
            </div>
            {EXCO_HIERARCHY.slice(1).map((tier) => (
              <div key={tier.tier} className="mb-12">
                <div className="overline-label mb-5">{tier.tier}</div>
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
                  {tier.roles.map((r) => <LeaderCard key={r.role} roleTitle={r.role} desc={r.desc} person={findPerson(r.role, "national")} />)}
                </div>
              </div>
            ))}
            {extraNational.length > 0 && (
              <div className="mb-12">
                <div className="overline-label mb-5">Additional Roles</div>
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
                  {extraNational.map((e) => <LeaderCard key={e.id} roleTitle={e.role} person={e} />)}
                </div>
              </div>
            )}
          </TabsContent>

          <TabsContent value="lga" className="mt-10" data-testid="exco-lga-section">
            <div className="mb-8 max-w-xs">
              <label className="text-sm text-zinc-400 mb-2 block">Select Local Government Area</label>
              <Select value={lga} onValueChange={setLga}>
                <SelectTrigger className="bg-[#131519] border-white/10" data-testid="exco-lga-selector"><SelectValue /></SelectTrigger>
                <SelectContent>{LGAS.map((l) => <SelectItem key={l} value={l}>{l}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <p className="text-sm text-zinc-500 mb-8">Note: at LGA level the Chairman is titled <span className="text-[#E6197E] font-medium">Local Government Coordinator</span>.</p>
            {EXCO_HIERARCHY.map((tier) => (
              <div key={tier.tier} className="mb-12">
                <div className="overline-label mb-5">{tier.tier}</div>
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
                  {tier.roles.map((r) => <LeaderCard key={r.role} roleTitle={r.lgaTitle || r.role} desc={r.desc} person={findPerson(r.role, "lga", lga)} />)}
                </div>
              </div>
            ))}
            {extraLga.length > 0 && (
              <div className="mb-12">
                <div className="overline-label mb-5">Additional Roles</div>
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
                  {extraLga.map((e) => <LeaderCard key={e.id} roleTitle={e.role} person={e} />)}
                </div>
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </main>
  );
}
